import { getDemoInfo, getDemoToken } from './demoSession';

function nowIso() {
  return new Date().toISOString();
}

function safeParseUrl(path) {
  try {
    return new URL(String(path || ''), 'http://local');
  } catch {
    return new URL('http://local/');
  }
}

function svgDataUrl({ title = 'Démo', subtitle = '', bg = '#0ea5e9' } = {}) {
  const t = String(title || '').slice(0, 42);
  const s = String(subtitle || '').slice(0, 64);
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="${bg}" stop-opacity="0.95"/>
      <stop offset="1" stop-color="#111827" stop-opacity="0.95"/>
    </linearGradient>
  </defs>
  <rect width="1200" height="800" fill="url(#g)"/>
  <circle cx="980" cy="240" r="220" fill="rgba(255,255,255,0.08)"/>
  <circle cx="260" cy="620" r="260" fill="rgba(255,255,255,0.07)"/>
  <text x="70" y="140" font-size="56" font-family="Inter, ui-sans-serif, system-ui" font-weight="700" fill="white">${escapeXml(
    t
  )}</text>
  <text x="70" y="210" font-size="26" font-family="Inter, ui-sans-serif, system-ui" fill="rgba(255,255,255,0.88)">${escapeXml(
    s
  )}</text>
  <text x="70" y="740" font-size="22" font-family="Inter, ui-sans-serif, system-ui" fill="rgba(255,255,255,0.65)">Aperçu démo — non sauvegardé</text>
</svg>`.trim();
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function escapeXml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

const MODULE_DEFS = {
  dashboard: { key: 'dashboard', label: 'Dashboard', route: '/dashboard', color: '#6d28d9', coming_soon: false },
  projects: { key: 'projects', label: 'Projets', route: '/projects', color: '#f97316', coming_soon: false },
  resources: { key: 'resources', label: 'Ressources', route: '/resources', color: '#0ea5e9', coming_soon: false },
  menus: { key: 'menus', label: 'Menus', route: '/menus', color: '#22c55e', coming_soon: false },
  scripts: { key: 'scripts', label: 'Scripts', route: '/scripts', color: '#9ca3af', coming_soon: false },
  team: { key: 'team', label: 'Équipe', route: '/team', color: '#a78bfa', coming_soon: false },
  languages: { key: 'languages', label: 'Langues', route: '/languages', color: '#22c55e', coming_soon: false },
  databases: { key: 'databases', label: 'Bases de données', route: '/databases', color: '#38bdf8', coming_soon: false },
  updates: { key: 'updates', label: 'Mises à jour', route: '/updates', color: '#8b5cf6', coming_soon: false },
  settings: { key: 'settings', label: 'Paramètres', route: '/settings', color: '#94a3b8', coming_soon: false },
  backups: { key: 'backups', label: 'Sauvegarde', route: '/backups', color: '#14b8a6', coming_soon: true },
  appointments: { key: 'appointments', label: 'Rendez-vous', route: '/appointments', color: '#ef4444', coming_soon: false },
};

const demoStateByToken = new Map();

function ensureDemoState(token) {
  const t = String(token || '').trim();
  if (!t) return null;
  const existing = demoStateByToken.get(t);
  if (existing) return existing;

  const createdAt = nowIso();
  const tags = [
    { id: 1, name: 'Démo', color: '#0ea5e9' },
    { id: 2, name: 'UI', color: '#a78bfa' },
    { id: 3, name: 'SEO', color: '#22c55e' },
    { id: 4, name: 'Marketing', color: '#f97316' },
    { id: 5, name: 'Backend', color: '#ef4444' }
  ];
  const categories = [
    { id: 1, name: 'Vitrine' },
    { id: 2, name: 'Landing' },
    { id: 3, name: 'Blog' },
  ];
  const styles = [
    { id: 1, name: 'Titre violet', class_name: 'demo-title-violet' },
    { id: 2, name: 'Texte muted', class_name: 'demo-muted' },
  ];

  const baseContent = () => ({
    version: 1,
    meta: {
      description: 'Ceci est un projet de démonstration. Les modifications ne sont pas enregistrées.',
      summary_lines: ['Démo du panel', 'Données factices', 'Aucune sauvegarde'],
      links: [
        { id: 'link_1', label: 'Site', url: 'https://example.com' },
        { id: 'link_2', label: 'Contact', url: 'mailto:hello@example.com' },
      ],
    },
    sections: [],
  });

  const projects = [
    {
      id: 101,
      name: "Site d'une startup poitevine",
      slug: 'site-startup-poitevine',
      cover_image_url: '',
      background_image_url: '',
      tags: [tags[0], tags[1], tags[3]],
      categories: [categories[0]],
      content: baseContent(),
      created_at: createdAt,
      updated_at: createdAt,
      has_translation: true,
      has_draft: true,
      draft_updated_at: createdAt,
      draft: null,
    },
    {
      id: 102,
      name: "Site vitrine d'une agence digitale bordelaise",
      slug: 'site-vitrine-agence-bordelaise',
      cover_image_url: '',
      background_image_url: '',
      tags: [tags[2]],
      categories: [categories[1]],
      content: baseContent(),
      created_at: createdAt,
      updated_at: createdAt,
      has_translation: true,
      has_draft: false,
      draft_updated_at: null,
      draft: null,
    },
  ];

  const media = [
    {
      id: 201,
      kind: 'image',
      mime_type: 'image/jpeg',
      variant: 'original',
      original_name: 'hero.jpg',
      note: 'Visuel hero (démo)',
      size_bytes: 245_120,
      is_hidden: 0,
      created_at: createdAt,
      updated_at: createdAt,
      links: [{ type: 'project', id: 101, is_pinned: 1 }],
      demo_src: svgDataUrl({ title: 'hero.jpg', subtitle: 'Image (démo)', bg: '#0ea5e9' }),
    },
    {
      id: 202,
      kind: 'image',
      mime_type: 'image/png',
      variant: 'original',
      original_name: 'gallery.png',
      note: 'Gallerie (démo)',
      size_bytes: 341_900,
      is_hidden: 0,
      created_at: createdAt,
      updated_at: createdAt,
      links: [{ type: 'project', id: 101, is_pinned: 0 }],
      demo_src: svgDataUrl({ title: 'gallery.png', subtitle: 'Image (démo)', bg: '#a78bfa' }),
    },
    {
      id: 203,
      kind: 'video',
      mime_type: 'video/mp4',
      variant: 'original',
      original_name: 'teaser.mp4',
      note: 'Teaser (démo)',
      size_bytes: 12_240_000,
      is_hidden: 0,
      created_at: createdAt,
      updated_at: createdAt,
      links: [{ type: 'project', id: 102, is_pinned: 0 }],
    },
    {
      id: 204,
      kind: 'document',
      mime_type: 'application/pdf',
      variant: 'original',
      original_name: 'brochure.pdf',
      note: 'Brochure (démo)',
      size_bytes: 920_500,
      is_hidden: 1,
      created_at: createdAt,
      updated_at: createdAt,
      links: [],
    },
  ];

  const appointments = [
    {
      id: 301,
      site_id: 1,
      client_id: 1,
      start_at: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16).replace('T', ' ') + ':00',
      location_type: 'agency',
      location_label: 'En agence',
      email_to: 'client@example.com',
      email_sent_at: nowIso().slice(0, 19).replace('T', ' '),
      email_last_error: '',
      created_by_user_id: 0,
      created_at: nowIso(),
      updated_at: nowIso(),
      client: { id: 1, company_name: 'Client démo', first_name: '', last_name: '', email: 'client@example.com' },
    },
  ];

  const updates = [
    {
      id: 401,
      site_id: 1,
      title: 'Optimisations du panel',
      level: 'minor',
      changes: ['Amélioration de l’UI des listes', 'Optimisation des performances sur mobile'],
      send_email: false,
      email_to: '',
      email_sent_at: null,
      email_last_error: '',
      created_by_user_id: 0,
      created_at: nowIso(),
      updated_at: nowIso(),
      created_by: { id: 0, display_name: 'Démo', email: '' },
    },
    {
      id: 402,
      site_id: 1,
      title: 'Mise à jour majeure du site',
      level: 'major',
      changes: ['Refonte de la page d’accueil', 'Correction de bugs critiques', 'Amélioration du SEO technique'],
      send_email: true,
      email_to: 'client@example.com',
      email_sent_at: nowIso().slice(0, 19).replace('T', ' '),
      email_last_error: '',
      created_by_user_id: 0,
      created_at: nowIso(),
      updated_at: nowIso(),
      created_by: { id: 0, display_name: 'Démo', email: '' },
    },
  ];

  const state = {
    createdAt,
    tags,
    categories,
    styles,
    projects,
    media,
    appointments,
    updates,
    menus: {
      version: 1,
      permanent: {
        categories: [
          {
            id: 'mc_demo_1',
            name: 'Entrées',
            description: 'Nos entrées du moment',
            items: [
              {
                id: 'mi_demo_1',
                title: 'Velouté de saison',
                description: 'Légumes frais, croûtons maison',
                price: '8,50 €',
                media_id: null,
                media_name: '',
                image_url: '',
                is_available: true,
              },
            ],
          },
        ],
      },
      formulas: [
        {
          id: 'mf_demo_1',
          title: 'Formule du midi',
          subtitle: 'Entrée + plat + café',
          description: 'Disponible du lundi au vendredi',
          price: '21,00 €',
          media_id: null,
          media_name: '',
          image_url: '',
          is_available: true,
        },
      ],
    },
    scripts: [
      {
        id: 801,
        site_id: 1,
        name: 'Récupération API publique',
        source_js: '',
        workflow: {
          version: 1,
          title: 'Récupération API publique',
          description: 'Exemple de workflow sécurisé.',
          network: { allowed_hosts: ['api.github.com'] },
          blocks: [
            {
              id: 'b_demo_1',
              type: 'http_request',
              method: 'GET',
              url: 'https://api.github.com/zen',
              save_as: 'api_result',
            },
            {
              id: 'b_demo_2',
              type: 'notify',
              message: 'Réponse reçue depuis {{api_result.host}}',
            },
          ],
        },
        permissions: {
          allow_http: true,
          allow_generate_files: true,
          allow_projects_read: true,
          allow_resources_read: false,
          allow_contacts_read: false,
          allow_updates_write: false,
          allow_notify: true,
          max_http_calls: 5,
          max_blocks: 100,
        },
        compiled: { parser: 'workflow.v1', parsed_at: nowIso() },
        is_enabled: true,
        sort_order: 0,
        last_run_at: null,
        created_at: nowIso(),
        updated_at: nowIso(),
        updated_by_user_id: 0,
        updated_by: { display_name: 'Démo', email: '' },
      },
    ],
    scriptRuns: [],
    panelSettings: {
      appearance: {
        theme_mode: 'dark',
        font: 'inter',
        background_preset_dark: 'midnight',
        background_preset_light: 'lavender',
        mobile_header_sticky: true,
      },
      projects: {
        default_view: 'editor',
        auto_expand_all_sections: true,
        section_open_mode: 'multi',
        container_open_mode: 'multi',
        show_image_preview: true,
        draft_mode: true,
        show_contrast_indicator: true,
        show_dynamic_tokens: true,
        shortcuts: { format_biu: true, save: true, publish: true },
      },
    },
  };
  demoStateByToken.set(t, state);
  return state;
}

function demoUserFromInfo(info) {
  const label = String(info?.label || '').trim();
  const clientName =
    String(info?.client?.company_name || '').trim() ||
    `${String(info?.client?.first_name || '').trim()} ${String(info?.client?.last_name || '').trim()}`.trim() ||
    String(info?.client?.email || '').trim() ||
    'Démo';
  return {
    id: 0,
    email: 'demo@omniscope.local',
    display_name: label || `Démo — ${clientName}`,
    role: 'client',
    is_demo: true,
  };
}

function demoSites() {
  return [{ id: 1, name: 'Site démo', domain: 'demo.local', member_role: 'owner' }];
}

function demoAllowedModules(info) {
  const raw = Array.isArray(info?.module_keys) ? info.module_keys : [];
  const keys = raw.map((k) => String(k || '').trim()).filter(Boolean);
  const out = [];
  for (const k of keys) {
    const def = MODULE_DEFS[k];
    if (def) out.push(def);
  }
  return out;
}

export function demoHandleFetch(path, { method = 'GET', body } = {}) {
  const token = getDemoToken();
  const info = getDemoInfo();
  const state = ensureDemoState(token);
  if (!token || !info || !state) {
    return { ok: false, status: 401, error: 'Démo non initialisée' };
  }

  const u = safeParseUrl(path);
  const pathname = String(u.pathname || '');
  const qs = u.searchParams;

  if (pathname.endsWith('/me.php')) {
    return { ok: true, status: 200, user: demoUserFromInfo(info), sites: demoSites() };
  }

  if (pathname.endsWith('/session_status.php')) {
    return { ok: true, status: 200, activeSessions: 1, multipleSessions: false };
  }

  if (pathname.endsWith('/logout.php')) {
    return { ok: true, status: 200 };
  }

  if (pathname.endsWith('/sites.php')) {
    return { ok: true, status: 200, sites: demoSites() };
  }

  if (pathname.endsWith('/modules.php')) {
    const modules = demoAllowedModules(info);
    return { ok: true, status: 200, modules };
  }

  if (pathname.endsWith('/site_languages.php')) {
    const languages = [
      { site_id: 1, lang_code: 'fr', label: 'Français', is_enabled: 1, is_default: 1, sort_order: 1 },
      { site_id: 1, lang_code: 'en', label: 'English', is_enabled: 1, is_default: 0, sort_order: 2 },
    ];
    return { ok: true, status: 200, default_lang: 'fr', languages };
  }

  if (pathname.endsWith('/panel_settings.php')) {
    if (method === 'GET') {
      return { ok: true, status: 200, user_id: 0, editable: true, settings: state.panelSettings };
    }
    if (method === 'PUT') {
      // no-op: demo
      return { ok: true, status: 200 };
    }
  }

  if (pathname.endsWith('/media.php')) {
    const siteId = Number(qs.get('site_id') || (body?.site_id ?? 0));
    if (siteId !== 1) return { ok: true, status: 200, assets: state.media };
    if (method === 'GET') return { ok: true, status: 200, assets: state.media };
    if (method === 'POST') return { ok: true, status: 200, assets: state.media };
    if (method === 'PUT') return { ok: true, status: 200, assets: state.media };
    if (method === 'DELETE') return { ok: true, status: 200, assets: state.media };
  }

  if (pathname.endsWith('/projects_ensure.php')) {
    return { ok: true, status: 200 };
  }

  if (pathname.endsWith('/projects.php')) {
    const projectId = Number(qs.get('id') || 0);
    const lang = String(qs.get('lang') || body?.lang || 'fr');
    const defaultLang = 'fr';

    if (method === 'GET') {
      if (projectId > 0) {
        const p = state.projects.find((x) => Number(x.id) === projectId) || null;
        if (!p) return { ok: false, status: 404, error: 'Projet introuvable' };
        const project = {
          ...p,
          tag_ids: (p.tags || []).map((t) => Number(t.id)),
          category_ids: (p.categories || []).map((c) => Number(c.id)),
        };
        return { ok: true, status: 200, project, tags: state.tags, categories: state.categories, styles: state.styles, lang, default_lang: defaultLang };
      }
      return { ok: true, status: 200, projects: state.projects, tags: state.tags, categories: state.categories, styles: state.styles, lang, default_lang: defaultLang };
    }

    if (method === 'POST') {
      return { ok: true, status: 201, id: Math.floor(1000 + Math.random() * 9000), lang, default_lang: defaultLang };
    }
    if (method === 'PUT') {
      return { ok: true, status: 200, draft_saved: true, lang, default_lang: defaultLang };
    }
    if (method === 'DELETE') {
      return { ok: true, status: 200 };
    }
  }

  if (pathname.endsWith('/databases.php')) {
    if (method === 'GET') {
      return {
        ok: true,
        status: 200,
        connections: [
          {
            id: 1,
            site_id: 1,
            host: 'demo-db.local',
            port: 3306,
            db_name: 'demo',
            user: 'demo',
            created_at: nowIso(),
            updated_at: nowIso(),
          },
        ],
      };
    }
    return { ok: true, status: 200 };
  }

  if (pathname.endsWith('/members.php')) {
    if (method === 'GET') {
      return {
        ok: true,
        status: 200,
        members: [
          { id: 1, membership_id: 1, user_id: 1, email: 'demo@omniscope.local', display_name: 'Compte démo', member_role: 'owner', is_active: true, modules: demoAllowedModules(info) },
        ],
        roles: [
          { member_role: 'owner', modules: demoAllowedModules(info) },
          { member_role: 'manager', modules: demoAllowedModules(info) },
          { member_role: 'editor', modules: demoAllowedModules(info) },
        ],
      };
    }
    return { ok: true, status: 200 };
  }

  if (pathname.endsWith('/appointments.php')) {
    if (method === 'GET') {
      return { ok: true, status: 200, appointments: state.appointments };
    }
    if (method === 'POST') {
      return { ok: true, status: 200, appointment: state.appointments[0] };
    }
    if (method === 'PUT') return { ok: true, status: 200 };
    if (method === 'DELETE') return { ok: true, status: 200 };
  }

  if (pathname.endsWith('/updates.php')) {
    if (method === 'GET') {
      return { ok: true, status: 200, updates: state.updates || [] };
    }
    if (method === 'POST') {
      const id = Math.floor(1000 + Math.random() * 9000);
      const title = String(body?.title || '').trim() || 'Mise à jour (démo)';
      const level = String(body?.level || 'minor') === 'major' ? 'major' : 'minor';
      const changes = Array.isArray(body?.changes)
        ? body.changes.map((x) => String(x || '').trim()).filter(Boolean).slice(0, 30)
        : String(body?.changes_text || '')
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter(Boolean)
            .slice(0, 30);
      const sendEmail = Boolean(body?.send_email) || level === 'major';
      const emailTo = sendEmail ? String(body?.email_to || 'client@example.com') : '';
      const item = {
        id,
        site_id: 1,
        title,
        level,
        changes,
        send_email: sendEmail,
        email_to: emailTo,
        email_sent_at: sendEmail ? nowIso().slice(0, 19).replace('T', ' ') : null,
        email_last_error: '',
        created_by_user_id: 0,
        created_at: nowIso(),
        updated_at: nowIso(),
        created_by: { id: 0, display_name: 'Démo', email: '' },
      };
      state.updates = [item, ...(state.updates || [])];
      return { ok: true, status: 200, update: item, mail: sendEmail ? { ok: true, error: '' } : null, email_required: level === 'major' };
    }
    if (method === 'PUT') {
      const id = Number(body?.id || 0);
      const nextTitle = String(body?.title || '').trim();
      const nextLevel = String(body?.level || '').trim() === 'major' ? 'major' : 'minor';
      const nextChanges = Array.isArray(body?.changes)
        ? body.changes.map((x) => String(x || '').trim()).filter(Boolean).slice(0, 40)
        : null;
      const list = Array.isArray(state.updates) ? state.updates : [];
      const idx = list.findIndex((u) => Number(u.id) === id);
      if (idx < 0) return { ok: false, status: 404, error: 'Mise à jour introuvable' };
      const cur = list[idx] || {};
      const updated = {
        ...cur,
        title: nextTitle || cur.title,
        level: nextLevel,
        changes: nextChanges || cur.changes || [],
        updated_at: nowIso(),
      };
      const next = [...list];
      next[idx] = updated;
      state.updates = next;
      return { ok: true, status: 200, update: updated };
    }
  }

  if (pathname.endsWith('/menus.php')) {
    if (method === 'GET') {
      return { ok: true, status: 200, menu: state.menus, updated_at: nowIso().slice(0, 19).replace('T', ' ') };
    }
    if (method === 'PUT') {
      const next = body?.menu && typeof body.menu === 'object' ? body.menu : state.menus;
      state.menus = next;
      return { ok: true, status: 200, menu: state.menus, updated_at: nowIso().slice(0, 19).replace('T', ' ') };
    }
  }

  if (pathname.endsWith('/scripts.php')) {
    if (method === 'GET') {
      const includeRuns = String(qs.get('include_runs') || '') === '1';
      const runLimitRaw = Number(qs.get('run_limit') || 20);
      const runLimit = Number.isFinite(runLimitRaw) ? Math.max(1, Math.min(100, Math.floor(runLimitRaw))) : 20;
      const runs = includeRuns ? (Array.isArray(state.scriptRuns) ? state.scriptRuns.slice(0, runLimit) : []) : [];
      return { ok: true, status: 200, scripts: Array.isArray(state.scripts) ? state.scripts : [], runs };
    }
    if (method === 'POST') {
      const next = {
        id: Math.max(800, ...((state.scripts || []).map((x) => Number(x?.id) || 0))) + 1,
        site_id: 1,
        name: String(body?.name || '').trim() || 'Nouveau script',
        source_js: String(body?.source_js || ''),
        workflow: body?.workflow && typeof body.workflow === 'object' ? body.workflow : { version: 1, title: 'Nouveau script', description: '', network: { allowed_hosts: [] }, blocks: [] },
        permissions:
          body?.permissions && typeof body.permissions === 'object'
            ? body.permissions
            : {
                allow_http: false,
                allow_generate_files: true,
                allow_projects_read: true,
                allow_resources_read: false,
                allow_contacts_read: false,
                allow_updates_write: false,
                allow_notify: true,
                max_http_calls: 5,
                max_blocks: 100,
              },
        compiled: body?.compiled && typeof body.compiled === 'object' ? body.compiled : null,
        is_enabled: body?.is_enabled !== false,
        sort_order: Number(body?.sort_order) || 0,
        last_run_at: null,
        created_at: nowIso(),
        updated_at: nowIso(),
        updated_by_user_id: 0,
        updated_by: { display_name: 'Démo', email: '' },
      };
      state.scripts = [next, ...(Array.isArray(state.scripts) ? state.scripts : [])];
      return { ok: true, status: 200, script: next };
    }
    if (method === 'PUT') {
      const id = Number(body?.id || 0);
      const list = Array.isArray(state.scripts) ? state.scripts : [];
      const idx = list.findIndex((x) => Number(x?.id) === id);
      if (idx < 0) return { ok: false, status: 404, error: 'Script introuvable' };
      const cur = list[idx] || {};
      const next = {
        ...cur,
        name: body?.name != null ? String(body.name || '').trim() : cur.name,
        source_js: body?.source_js != null ? String(body.source_js || '') : cur.source_js,
        workflow: body?.workflow && typeof body.workflow === 'object' ? body.workflow : cur.workflow,
        permissions: body?.permissions && typeof body.permissions === 'object' ? body.permissions : cur.permissions,
        compiled: body?.compiled && typeof body.compiled === 'object' ? body.compiled : cur.compiled,
        is_enabled: body?.is_enabled == null ? cur.is_enabled : Boolean(body.is_enabled),
        sort_order: body?.sort_order == null ? cur.sort_order : Number(body.sort_order) || 0,
        updated_at: nowIso(),
      };
      const out = [...list];
      out[idx] = next;
      state.scripts = out;
      return { ok: true, status: 200, script: next };
    }
    if (method === 'DELETE') {
      const id = Number(body?.id || 0);
      state.scripts = (Array.isArray(state.scripts) ? state.scripts : []).filter((x) => Number(x?.id) !== id);
      return { ok: true, status: 200 };
    }
  }

  if (pathname.endsWith('/scripts_compile.php') && method === 'POST') {
    const sourceJs = String(body?.source_js || '').trim();
    if (!sourceJs) return { ok: false, status: 422, error: 'source_js requis' };
    if (!/[a-zA-Z0-9]/.test(sourceJs) || sourceJs.length < 8) {
      return { ok: false, status: 422, error: 'Conversion JS impossible: contenu non interprétable' };
    }
    return {
      ok: true,
      status: 200,
      source_js: sourceJs,
      workflow: {
        version: 1,
        title: 'Script importé',
        description: '',
        network: { allowed_hosts: [] },
        blocks: [{ id: `b_demo_${Date.now()}`, type: 'notify', message: 'Script importé (démo)' }],
      },
    };
  }

  if (pathname.endsWith('/scripts_run.php')) {
    if (method === 'DELETE') {
      const scriptId = Number(body?.script_id || body?.id || 0);
      const before = Array.isArray(state.scriptRuns) ? state.scriptRuns : [];
      if (scriptId > 0) {
        state.scriptRuns = before.filter((x) => Number(x?.script_id) !== scriptId);
        state.scripts = (Array.isArray(state.scripts) ? state.scripts : []).map((s) =>
          Number(s?.id) === scriptId ? { ...(s || {}), last_run_at: null } : s
        );
      } else {
        state.scriptRuns = [];
        state.scripts = (Array.isArray(state.scripts) ? state.scripts : []).map((s) => ({ ...(s || {}), last_run_at: null }));
      }
      return { ok: true, status: 200, deleted: Math.max(0, before.length - state.scriptRuns.length) };
    }

    if (method === 'POST') {
      const scriptId = Number(body?.script_id || body?.id || 0);
      const script = (Array.isArray(state.scripts) ? state.scripts : []).find((x) => Number(x?.id) === scriptId) || null;
      if (!script) return { ok: false, status: 404, error: 'Script introuvable' };
      if (script.is_enabled === false) return { ok: false, status: 409, error: 'Script désactivé' };

      const input = body?.input && typeof body.input === 'object' ? body.input : {};
      const run = {
        id: Math.max(0, ...((state.scriptRuns || []).map((x) => Number(x?.id) || 0))) + 1,
        script_id: scriptId,
        status: 'success',
        started_at: nowIso().slice(0, 19).replace('T', ' '),
        finished_at: nowIso().slice(0, 19).replace('T', ' '),
        duration_ms: 36,
        error_text: null,
        executed_by: 'Démo',
      };
      state.scriptRuns = [run, ...(Array.isArray(state.scriptRuns) ? state.scriptRuns : [])];
      script.last_run_at = run.finished_at;
      script.updated_at = nowIso();

      return {
        ok: true,
        status: 200,
        run,
        output: {
          vars: { ...input, demo_echo: true, script_name: script.name || 'Script démo' },
          generated_files: [],
          displays: [],
          logs: ['#1 notify: exécution démo', 'Aucune écriture persistée en mode démo'],
        },
      };
    }
  }

  if (pathname.endsWith('/appointment_public.php')) {
    if (method === 'GET') return { ok: true, status: 200, appointment: state.appointments[0] };
    if (method === 'POST') return { ok: true, status: 200, appointment: state.appointments[0] };
  }

  // fallback: no-op ok
  return { ok: true, status: 200 };
}

export function demoHandleUpload() {
  const token = getDemoToken();
  const info = getDemoInfo();
  const state = ensureDemoState(token);
  if (!token || !info || !state) return { ok: false, status: 401, error: 'Démo non initialisée' };
  return { ok: true, status: 200, assets: state.media, created: [], rejected: [] };
}
